import React, { useState, useEffect } from 'react';
import Modal from '../Common/Modal';
import Tabs from '../Common/Tabs';
import FileViewer from '../Common/FileViewer';
import PDFViewer from '../Common/PDFViewer';
import ImageViewer from '../Common/ImageViewer';
import { useAuth } from '../Auth/AuthContext';
import { orderToasts, showToast } from '../../utils/toastUtils';
import { PDFDocument } from 'pdf-lib';
import DocumentSplitView from './DocumentSplitView';
import UploadJobsPanel from './UploadJobsPanel';

// Copy button component for copying text to clipboard
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      showToast.error('Failed to copy');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-1 text-gray-400 hover:text-indigo-600 transition-colors"
      title={`Copy ${label || 'value'}`}
      type="button"
    >
      {copied ? (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
};

const OrderModal = ({
  order,
  isOpen = true,
  loadingDetails = false,
  onClose,
  vendors,
  onAssignOrder,
  onApproveRejectSample,
  onFileUpload,
  onOrderUpdate,
  onEditOrder,
  onRejectOrder,
  onCompleteOrder,
  onUploadFullOrderDocument
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [viewingFile, setViewingFile] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [uploading, setUploading] = useState(false);
  const [localOrder, setLocalOrder] = useState(order);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showSampleConfirmation, setShowSampleConfirmation] = useState(false);
  const [selectedSampleFile, setSelectedSampleFile] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editFormData, setEditFormData] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [pagesPerStamp, setPagesPerStamp] = useState('1');
  const [csvFile, setCsvFile] = useState(null); // Admin-only: CSV for certificate IDs
  const [loadingFiles, setLoadingFiles] = useState({}); // New state for tracking loading files
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [pdfViewerData, setPdfViewerData] = useState({ url: null, title: '' });
  const [selectedFiles, setSelectedFiles] = useState([]); // State for bulk selection
  const [sampleAcknowledged, setSampleAcknowledged] = useState(false);
  const [showPodConfirmation, setShowPodConfirmation] = useState(false);
  const [selectedPodFile, setSelectedPodFile] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [imageViewerData, setImageViewerData] = useState({ url: null, title: '' });

  const indianStates = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
    "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
    "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
    "Uttarakhand", "West Bengal",
    "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
  ];

  useEffect(() => {
    setLocalOrder(order);
    setEditFormData({
      article: order.article || '',
      quantity: order.quantity || 1,
      denomination: order.denomination || 0,
      notes: order.notes || '',
      state: order.state || '',
      delivery_mode: order.delivery_mode || 'digital',
      first_party: {
        name: order.first_party?.name || '',
        address: order.first_party?.address || '',
        mobile: order.first_party?.mobile || ''
      },
      second_party: {
        name: order.second_party?.name || '',
        address: order.second_party?.address || '',
        mobile: order.second_party?.mobile || ''
      }
    });
  }, [order]);

  useEffect(() => {
    return () => {
      if (viewingFile?.file) {
        URL.revokeObjectURL(viewingFile.file);
      }
    };
  }, [viewingFile]);

  if (!localOrder) return null;

  const getStatusColor = (status) => {
    const colors = {
      placed: 'bg-blue-100 text-blue-800',
      assigned: 'bg-yellow-100 text-yellow-800',
      sample_submitted: 'bg-purple-100 text-purple-800',
      sample_approved: 'bg-green-100 text-green-800',
      sample_rejected: 'bg-red-100 text-red-800',
      executing: 'bg-orange-100 text-orange-800',
      completed: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getSampleStatusBadge = () => {
    if (!localOrder.sample_file) return null;
    if (localOrder.status === 'sample_approved') {
      return { text: 'Approved', color: 'bg-green-100 text-green-800' };
    } else if (localOrder.status === 'sample_rejected') {
      return { text: 'Rejected', color: 'bg-red-100 text-red-800' };
    } else if (localOrder.status === 'sample_submitted') {
      return { text: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' };
    }
    return null;
  };

  const handleSampleSelect = async (file) => {
    if (!file) return;

    // Validate file type and page limits
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      showToast.error('Please upload an image or PDF file');
      return;
    }

    // Images are always 1 page - allowed
    if (isImage) {
      const url = URL.createObjectURL(file);
      setSelectedSampleFile({ file, url, type: file.type });
      setSampleAcknowledged(false);
      return;
    }

    // For PDFs, check page count (max 2 pages)
    if (isPdf) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pageCount = pdfDoc.getPageCount();

        if (pageCount > 2) {
          showToast.error(`Sample PDF must have at most 2 pages. This file has ${pageCount} pages.`);
          return;
        }

        const url = URL.createObjectURL(file);
        setSelectedSampleFile({ file, url, type: file.type });
        setSampleAcknowledged(false);
      } catch (error) {
        console.error('Error reading PDF:', error);
        showToast.error('Unable to read PDF file. Please try a different file.');
      }
    }
  };

  const handleSampleCancel = () => {
    if (selectedSampleFile?.url) {
      URL.revokeObjectURL(selectedSampleFile.url);
    }
    setSelectedSampleFile(null);
    setSampleAcknowledged(false);
  };

  const handleSubmitSample = async () => {
    if (selectedSampleFile && sampleAcknowledged) {
      await handleFileUpload('sample', selectedSampleFile.file);
      handleSampleCancel();
    }
  };

  const getTabs = () => {
    const tabs = [
      { id: 'details', label: 'Order Details', icon: '📋' }
    ];
    // Sample tab visible to all roles
    tabs.push({
      id: 'sample',
      label: 'Sample',
      icon: '🎨',
      badge: getSampleStatusBadge()
    });
    const isSampleApproved = ['sample_approved', 'completed', 'delivered'].includes(localOrder.status);

    if ((localOrder.delivery_mode === 'digital' || localOrder.delivery_mode === 'both') && isSampleApproved) {
      tabs.push({
        id: 'full-document',
        label: 'Full Order Documents',
        icon: '📑'
      });
    }
    // Add API Documents tab if digital/both and sample approved. Hide for Vendor.
    if ((localOrder.delivery_mode === 'digital' || localOrder.delivery_mode === 'both') && isSampleApproved && user.role !== 'vendor') {
      tabs.push({
        id: 'api-documents',
        label: 'API Documents',
        icon: '🧩'
      });
    }
    // Add Upload Jobs tab for tracking OCR uploads if digital/both and sample approved. Hide for Client.
    if ((localOrder.delivery_mode === 'digital' || localOrder.delivery_mode === 'both' || localOrder.delivery_mode === 'physical') && isSampleApproved && user.role !== 'client') {
      tabs.push({
        id: 'upload-jobs',
        label: 'Upload Jobs',
        icon: '📤'
      });
    }
    if (localOrder.pod_file) {
      tabs.push({
        id: 'pod',
        label: 'Proof of Delivery',
        icon: '📦'
      });
    }
    if (user.role === 'admin' ||
      (user.role === 'vendor' && localOrder.vendor_id === user.id) ||
      (user.role === 'client' && localOrder.client_id === user.id)) {
      tabs.push({
        id: 'actions',
        label: 'Actions',
        icon: '⚡'
      });
    }
    return tabs;
  };

  const handleFilePreview = async (orderId, fileId, filename) => {
    try {
      setLoadingFiles((prev) => ({ ...prev, [fileId]: true })); // Set loading state for this file
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/full-document/${fileId}/download`,
        {
          credentials: 'include',
        }
      );
      if (!response.ok) throw new Error('Failed to fetch file');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setViewingFile({ file: url, filename });
    } catch (error) {
      console.error('Error previewing file:', error);
      showToast.error('Failed to load file for preview');
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [fileId]: false })); // Clear loading state
    }
  };

  const handleFileDownload = async (orderId, fileId, filename) => {
    try {
      setLoadingFiles((prev) => ({ ...prev, [`${fileId}_download`]: true })); // Set loading state for download
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/full-document/${fileId}/download`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error('Failed to download file');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Create temporary download link and trigger download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast.success(`File "${filename}" downloaded successfully`);
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast.error('Failed to download file');
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [`${fileId}_download`]: false })); // Clear loading state
    }
  };

  const handleLegacyFileDownload = (base64Data, filename) => {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast.success(`File "${filename}" downloaded successfully`);
    } catch (error) {
      console.error('Error downloading legacy file:', error);
      showToast.error('Failed to download legacy file');
    }
  };

  const getPageCount = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      return pdfDoc.getPageCount();
    } catch (error) {
      console.error('Error reading PDF page count:', error);
      return 0;
    }
  };

  const handlePDFPreview = (file, filename) => {
    let pdfUrl;
    if (typeof file === 'string') {
      // If file is a base64 string, convert to blob URL
      const byteCharacters = atob(file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      pdfUrl = URL.createObjectURL(blob);
    } else {
      // If file is already a blob URL
      pdfUrl = file;
    }

    setPdfViewerData({ url: pdfUrl, title: filename });
    setShowPDFViewer(true);
  };

  const handleClosePDFViewer = () => {
    setShowPDFViewer(false);
    if (pdfViewerData.url) {
      URL.revokeObjectURL(pdfViewerData.url);
    }
    setPdfViewerData({ url: null, title: '' });
  };

  const handlePodSelect = (file) => {
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      showToast.error('Please upload an image or PDF file');
      return;
    }

    const url = URL.createObjectURL(file);
    setSelectedPodFile({ file, url, type: file.type, name: file.name, size: file.size });
    setShowPodConfirmation(true);
  };

  const handlePodCancel = () => {
    if (selectedPodFile?.url) {
      URL.revokeObjectURL(selectedPodFile.url);
    }
    setSelectedPodFile(null);
    setShowPodConfirmation(false);
  };

  const confirmPodUpload = async () => {
    if (!selectedPodFile) return;
    setUploading(true);
    try {
      await onFileUpload(localOrder.id, 'pod', selectedPodFile.file);
      orderToasts.podUploaded();
    } catch (error) {
      orderToasts.uploadError();
    } finally {
      setUploading(false);
      setShowPodConfirmation(false);
      if (selectedPodFile?.url) {
        URL.revokeObjectURL(selectedPodFile.url);
      }
      setSelectedPodFile(null);
    }
  };

  const handlePodPreview = (file, filename, customUrl = null) => {
    let fileUrl = customUrl;
    let isImage = false;

    if (!fileUrl) {
      if (typeof file === 'string') {
        // If file is a base64 string, guess type and convert to blob URL
        let mimeType = 'application/pdf';
        if (filename && filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/)) {
          mimeType = `image/${filename.split('.').pop().toLowerCase()}`;
          isImage = true;
        } else if (file.startsWith('JVBER')) {
          // Starts with %PDF (base64)
          mimeType = 'application/pdf';
        }

        const byteCharacters = atob(file);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        fileUrl = URL.createObjectURL(blob);
      } else {
        // If file is already a URL or something else
        fileUrl = file;
        if (filename && filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
          isImage = true;
        }
      }
    } else {
      if (selectedPodFile && selectedPodFile.type.startsWith('image/')) {
        isImage = true;
      } else if (filename && filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
        isImage = true;
      }
    }

    if (isImage) {
      setImageViewerData({ url: fileUrl, title: filename });
      setShowImageViewer(true);
    } else {
      setPdfViewerData({ url: fileUrl, title: filename });
      setShowPDFViewer(true);
    }
  };

  const handleCloseImageViewer = () => {
    setShowImageViewer(false);
    if (imageViewerData.url && imageViewerData.url !== selectedPodFile?.url) {
      // Only revoke if it's not the currently selected pod file's url, 
      // as that would break the preview in the confirmation dialog if reopened.
      URL.revokeObjectURL(imageViewerData.url);
    }
    setImageViewerData({ url: null, title: '' });
  };

  const handleFullDocumentPreview = async (orderId, fileId, filename) => {
    try {
      setLoadingFiles((prev) => ({ ...prev, [fileId]: true }));
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/full-document/${fileId}/download`,
        {
          credentials: 'include',
        }
      );
      if (!response.ok) throw new Error('Failed to fetch file');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfViewerData({ url, title: filename });
      setShowPDFViewer(true);
    } catch (error) {
      console.error('Error previewing file:', error);
      showToast.error('Failed to load file for preview');
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [fileId]: false }));
    }
  };

  const handleFileUpload = async (fileType, files, pagesPerStamp = 1) => {
    // Legacy interception removed
    if (fileType === 'full-document') {
      // Pre-check: prevent upload if stamp quantity is already met
      const currentStamps = (localOrder.full_order_document_files || []).reduce(
        (sum, f) => sum + (f.stamp_count || f.page_count || 0), 0
      );
      if (currentStamps >= localOrder.quantity) {
        showToast.error(`Upload blocked: All ${localOrder.quantity} stamps have already been uploaded.`);
        return;
      }
      setUploading(true);
      try {
        for (const file of files) {
          if (pagesPerStamp === 2) {
            const pageCount = await getPageCount(file);
            if (pageCount % 2 !== 0) {
              showToast.error('This PDF has an odd number of pages. Double-sided documents must contain an even number of pages.');
              setUploading(false);
              return;
            }
          }
        }
        // Pass csvFile for admins
        await onUploadFullOrderDocument(localOrder.id, files, pagesPerStamp, user.role === 'admin' ? csvFile : null);
        showToast.success(`Successfully uploaded ${files.length} document(s)`);
        setShowUploadDialog(false);
        setUploadFiles([]);
        setPagesPerStamp('1');
        setCsvFile(null); // Reset CSV file after upload
      } catch (error) {
        orderToasts.uploadError();
      } finally {
        setUploading(false);
      }
      return;
    }

    setUploading(true);
    try {
      await onFileUpload(localOrder.id, fileType, files);
      if (fileType === 'sample') {
        orderToasts.sampleSubmitted();
      } else if (fileType === 'pod') {
        orderToasts.podUploaded();
      }
    } catch (error) {
      orderToasts.uploadError();
    } finally {
      setUploading(false);
    }
  };

  const confirmSampleUpload = async () => {
    if (!selectedSampleFile) return;
    setUploading(true);
    try {
      await onFileUpload(localOrder.id, 'sample', selectedSampleFile);
      orderToasts.sampleSubmitted();
    } catch (error) {
      orderToasts.uploadError();
    } finally {
      setUploading(false);
      setShowSampleConfirmation(false);
      setSelectedSampleFile(null);
    }
  };

  const handleFileApproval = async (fileId, approved) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${localOrder.id}/full-document/${fileId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_id: fileId, approved }),
      });
      if (response.ok) {
        showToast.success(`File ${approved ? 'approved' : 'rejected'} successfully`);
        window.location.reload();
      } else {
        throw new Error('Approval failed');
      }
    } catch (error) {
      console.error('Error approving file:', error);
      showToast.error('Failed to update file approval status');
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${localOrder.id}/full-document/${fileId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        showToast.success('File deleted successfully');
        window.location.reload();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      showToast.error('Failed to delete file');
    }
  };

  // Bulk selection handlers
  const handleSelectFile = (fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSelectAll = (files) => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(f => f.id));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedFiles.length === 0) return;
    if (!window.confirm(`Approve ${selectedFiles.length} selected file(s)?`)) return;

    try {
      await Promise.all(
        selectedFiles.map(fileId =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${localOrder.id}/full-document/${fileId}/approve`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ file_id: fileId, approved: true }),
          })
        )
      );
      showToast.success(`${selectedFiles.length} file(s) approved successfully`);
      setSelectedFiles([]);
      window.location.reload();
    } catch (error) {
      console.error('Error bulk approving files:', error);
      showToast.error('Failed to approve files');
    }
  };

  const handleBulkReject = async () => {
    if (selectedFiles.length === 0) return;
    if (!window.confirm(`Reject ${selectedFiles.length} selected file(s)?`)) return;

    try {
      await Promise.all(
        selectedFiles.map(fileId =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${localOrder.id}/full-document/${fileId}/approve`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ file_id: fileId, approved: false }),
          })
        )
      );
      showToast.success(`${selectedFiles.length} file(s) rejected successfully`);
      setSelectedFiles([]);
      window.location.reload();
    } catch (error) {
      console.error('Error bulk rejecting files:', error);
      showToast.error('Failed to reject files');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.length === 0) return;
    if (!window.confirm(`Delete ${selectedFiles.length} selected file(s)? This action cannot be undone.`)) return;

    try {
      await Promise.all(
        selectedFiles.map(fileId =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${localOrder.id}/full-document/${fileId}`, {
            method: 'DELETE',
            credentials: 'include',
          })
        )
      );
      showToast.success(`${selectedFiles.length} file(s) deleted successfully`);
      setSelectedFiles([]);
      window.location.reload();
    } catch (error) {
      console.error('Error bulk deleting files:', error);
      showToast.error('Failed to delete files');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      for (const fileId of selectedFiles) {
        const fileInfo = visibleFiles.find(f => f.id === fileId);
        if (!fileInfo) continue;

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/orders/${localOrder.id}/full-document/${fileId}/download`,
          {
            credentials: 'include',
          }
        );

        if (!response.ok) continue;

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileInfo.filename;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      showToast.success(`${selectedFiles.length} file(s) downloaded successfully`);
    } catch (error) {
      console.error('Error bulk downloading files:', error);
      showToast.error('Failed to download some files');
    }
  };


  const handleAssignOrder = async () => {
    if (selectedVendor) {
      try {
        await onAssignOrder(localOrder.id, selectedVendor);
        const vendorName = vendors.find(v => v.id === selectedVendor)?.name || 'Vendor';
        orderToasts.assigned(vendorName);
        setSelectedVendor('');
      } catch (error) {
        orderToasts.assignmentError();
      }
    }
  };

  const renderContent = () => {
    if (viewingFile) {
      return (
        <FileViewer
          file={viewingFile.file}
          filename={viewingFile.filename}
          onBack={() => {
            if (viewingFile.file) {
              URL.revokeObjectURL(viewingFile.file);
            }
            setViewingFile(null);
          }}
        />
      );
    }

    switch (activeTab) {
      case 'details':
        return (
          <div className="px-6 pb-6 pt-2 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-on-light mb-3">Order Information</h4>
                  <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                    <tbody className="divide-y divide-gray-200">
                      <tr className="bg-gray-200">
                        <td className="px-4 py-2 font-medium text-black w-1/3">Order ID</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span>{localOrder.id}</span>
                          <CopyButton text={localOrder.id} label="Order ID" />
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-4 py-2 font-medium text-black">Article</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span>{localOrder.article}</span>
                          <CopyButton text={localOrder.article} label="Article" />
                        </td>
                      </tr>
                      <tr className="bg-gray-200">
                        <td className="px-4 py-2 font-medium text-black">State</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span>{localOrder.state}</span>
                          <CopyButton text={localOrder.state} label="State" />
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-4 py-2 font-medium text-black">Quantity</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span>{localOrder.quantity}</span>
                          <CopyButton text={localOrder.quantity} label="Quantity" />
                        </td>
                      </tr>
                      <tr className="bg-gray-200">
                        <td className="px-4 py-2 font-medium text-black">Denomination</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span>₹{localOrder.denomination}</span>
                          <CopyButton text={localOrder.denomination} label="Denomination" />
                        </td>
                      </tr>
                      {localOrder.consideration_amount && (
                        <tr className="bg-white">
                          <td className="px-4 py-2 font-medium text-black">Consideration Amount (₹)</td>
                          <td className="px-4 py-2 text-black flex items-center justify-between">
                            <span>₹{localOrder.consideration_amount}</span>
                            <CopyButton text={localOrder.consideration_amount} label="Consideration Amount" />
                          </td>
                        </tr>
                      )}
                      <tr className={localOrder.consideration_amount ? "bg-gray-200" : "bg-white"}>
                        <td className="px-4 py-2 font-medium text-black">Purchased/Paid By</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span>{localOrder.purchased_paid_by === 'second_party' ? 'Second Party' : 'First Party'}</span>
                          <CopyButton text={localOrder.purchased_paid_by === 'second_party' ? 'Second Party' : 'First Party'} label="Purchased/Paid By" />
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-4 py-2 font-medium text-black">Delivery Mode</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span>{localOrder.delivery_mode}</span>
                          <CopyButton text={localOrder.delivery_mode} label="Delivery Mode" />
                        </td>
                      </tr>
                      {(localOrder.delivery_mode === 'physical' || localOrder.delivery_mode === 'both') && localOrder.delivery_address && (
                        <tr className="bg-gray-200">
                          <td className="px-4 py-2 font-medium text-black">Delivery Address</td>
                          <td className="px-4 py-2 text-black flex items-center justify-between">
                            <span>{localOrder.delivery_address}</span>
                            <CopyButton text={localOrder.delivery_address} label="Delivery Address" />
                          </td>
                        </tr>
                      )}
                      <tr className="bg-gray-200">
                        <td className="px-4 py-2 font-medium text-black">Status</td>
                        <td className="px-4 py-2 text-black flex items-center justify-between">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(localOrder.status)}`}>
                            {localOrder.status.replace('_', ' ')}
                          </span>
                          <CopyButton text={localOrder.status.replace('_', ' ')} label="Status" />
                        </td>
                      </tr>
                      {user.role === 'admin' && (
                        <>
                          <tr className="bg-white">
                            <td className="px-4 py-2 font-medium text-black">Client</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.client_name}</span>
                              <CopyButton text={localOrder.client_name} label="Client" />
                            </td>
                          </tr>
                          <tr className="bg-gray-200">
                            <td className="px-4 py-2 font-medium text-black">Vendor</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.vendor_name || 'Unassigned'}</span>
                              <CopyButton text={localOrder.vendor_name || 'Unassigned'} label="Vendor" />
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
                {localOrder.notes && (
                  <div>
                    <h4 className="font-semibold text-on-light mb-3">Notes</h4>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <p className="text-sm text-black">{localOrder.notes}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-on-light mb-3">Party Details</h4>
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-medium text-black mb-3">First Party</p>
                      <table className="w-full border border-blue-200 rounded-lg overflow-hidden">
                        <tbody className="divide-y divide-blue-200">
                          <tr className="bg-blue-200">
                            <td className="px-4 py-2 font-medium text-black w-1/3">Name</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.first_party.name}</span>
                              <CopyButton text={localOrder.first_party.name} label="First Party Name" />
                            </td>
                          </tr>
                          <tr className="bg-blue-50">
                            <td className="px-4 py-2 font-medium text-black">Address</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.first_party.address}</span>
                              <CopyButton text={localOrder.first_party.address} label="First Party Address" />
                            </td>
                          </tr>
                          <tr className="bg-blue-200">
                            <td className="px-4 py-2 font-medium text-black">Mobile</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.first_party.mobile}</span>
                              <CopyButton text={localOrder.first_party.mobile} label="First Party Mobile" />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="font-medium text-black mb-3">Second Party</p>
                      <table className="w-full border border-green-200 rounded-lg overflow-hidden">
                        <tbody className="divide-y divide-green-200">
                          <tr className="bg-green-200">
                            <td className="px-4 py-2 font-medium text-black w-1/3">Name</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.second_party.name}</span>
                              <CopyButton text={localOrder.second_party.name} label="Second Party Name" />
                            </td>
                          </tr>
                          <tr className="bg-green-50">
                            <td className="px-4 py-2 font-medium text-black">Address</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.second_party.address}</span>
                              <CopyButton text={localOrder.second_party.address} label="Second Party Address" />
                            </td>
                          </tr>
                          <tr className="bg-green-200">
                            <td className="px-4 py-2 font-medium text-black">Mobile</td>
                            <td className="px-4 py-2 text-black flex items-center justify-between">
                              <span>{localOrder.second_party.mobile}</span>
                              <CopyButton text={localOrder.second_party.mobile} label="Second Party Mobile" />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'sample':
        // Helper to render embedded sample preview
        const renderEmbeddedSample = () => {
          // 1. Initial State: No file uploaded yet OR Sample Rejected (Show Upload UI)
          if ((!localOrder.sample_file && !selectedSampleFile) || (localOrder.status === 'sample_rejected' && !selectedSampleFile)) {
            // Only show upload UI if user has permission (Admin or Vendor assigned to order)
            if (user.role === 'admin' || (user.role === 'vendor' && localOrder.vendor_id === user.id)) {
              return (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50 text-gray-500 p-6 text-center">
                  <div className="mb-4 p-4 bg-white rounded-full shadow-sm">
                    <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <h5 className="font-medium text-gray-900 mb-1">
                    {localOrder.status === 'sample_rejected' ? 'Previous Sample Rejected' : 'Upload Initial Sample'}
                  </h5>
                  <p className="text-sm text-gray-500 mb-4 max-w-xs">
                    {localOrder.status === 'sample_rejected'
                      ? 'Please upload a new version.'
                      : 'Drag and drop or click to browse'}
                  </p>

                  <label className="cursor-pointer">
                    <span className="btn-primary px-4 py-2 text-sm shadow-sm hover:shadow transition-all">
                      Browse Files
                    </span>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => handleSampleSelect(e.target.files[0])}
                      disabled={uploading}
                    />
                  </label>
                  <p className="text-xs text-gray-400 mt-2">Supports PDF, PNG, JPG (Max 2 pages)</p>
                </div>
              );
            } else {
              // Client view when no sample
              return (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50 text-gray-400">
                  <svg className="w-12 h-12 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">Waiting for vendor to upload sample...</p>
                </div>
              );
            }
          }

          // 2. Active File State: Either Selected (Pre-submit) OR Existing (Submitted/Approved/Rejected)
          const isNewFile = !!selectedSampleFile;
          const fileSource = isNewFile ? selectedSampleFile.url : (localOrder.sample_file || '');
          const fileName = isNewFile ? selectedSampleFile.file.name : (localOrder.sample_filename || 'sample.pdf');

          let isPdf = false;
          if (isNewFile) {
            isPdf = selectedSampleFile.type === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
          } else {
            const b64 = fileSource;
            const looksPdfByHeader = typeof b64 === 'string' && b64.startsWith('JVBERi0');
            const isPdfByExt = fileName.toLowerCase().endsWith('.pdf');
            isPdf = looksPdfByHeader || isPdfByExt;
          }

          const renderPreviewContent = () => {
            if (isPdf) {
              const pdfDataUrl = isNewFile ? fileSource : `data:application/pdf;base64,${fileSource}`;
              return <embed src={pdfDataUrl} type="application/pdf" className="w-full h-full" />;
            } else {
              let mime = 'image/*';
              if (fileName.toLowerCase().endsWith('.png')) mime = 'image/png';
              else if (fileName.toLowerCase().endsWith('.jpg') || fileName.toLowerCase().endsWith('.jpeg')) mime = 'image/jpeg';
              else if (fileName.toLowerCase().endsWith('.gif')) mime = 'image/gif';
              const imgDataUrl = isNewFile ? fileSource : `data:${mime};base64,${fileSource}`;
              return (
                <div className="w-full h-full">
                  <ImageViewer src={imgDataUrl} alt="Sample Preview" fileName={fileName} />
                </div>
              );
            }
          };

          return (
            <div className="relative w-full h-full group">
              {/* Main Preview */}
              {renderPreviewContent()}



              {/* OVERLAY: Submission Form (Bottom) - Only for New File Upload */}
              {isNewFile && (
                <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 p-4 shadow-lg animate-slide-up">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]" title={fileName}>
                      {fileName}
                    </span>
                    <button
                      onClick={handleSampleCancel}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Cancel Upload"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-start space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sampleAcknowledged}
                        onChange={(e) => setSampleAcknowledged(e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                      />
                      <span className="text-xs text-gray-600 leading-tight">
                        I confirm this sample is accurate and ready for client review.
                      </span>
                    </label>

                    <button
                      onClick={handleSubmitSample}
                      disabled={!sampleAcknowledged || uploading}
                      className="w-full btn-primary py-2 text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        'Submit Sample'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* OVERLAY: Status Badge (Top Left) - For existing files */}
              {!isNewFile && (
                <div className="absolute top-2 left-2">
                  {/* Status badge logic moved here if needed, or use the header logic */}
                </div>
              )}
            </div>
          );
        };

        return (
          <div className="px-6 pb-6 pt-2 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h4 className="text-lg font-semibold text-gray-800">Sample Evaluation</h4>
              {getSampleStatusBadge() && (
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSampleStatusBadge().color.replace('bg-', 'border-').replace('text-', 'text-')} bg-white`}>
                  {getSampleStatusBadge().text}
                </span>
              )}
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">

              {/* LEFT COLUMN: Order Details (UNCHANGED as requested) */}
              <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                {/* Order Information */}
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2 text-sm">Order Information</h5>
                  <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm">
                    <tbody className="divide-y divide-gray-200">
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-600 w-1/3">Order ID</td>
                        <td className="px-3 py-2 text-gray-900 font-mono">{localOrder.id}</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-3 py-2 font-medium text-gray-600">Article</td>
                        <td className="px-3 py-2 text-gray-900">{localOrder.article}</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-600">State</td>
                        <td className="px-3 py-2 text-gray-900">{localOrder.state}</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-3 py-2 font-medium text-gray-600">Quantity</td>
                        <td className="px-3 py-2 text-gray-900">{localOrder.quantity}</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-600">Denomination</td>
                        <td className="px-3 py-2 text-gray-900">₹{localOrder.denomination}</td>
                      </tr>
                      {localOrder.consideration_amount && (
                        <tr className="bg-white">
                          <td className="px-3 py-2 font-medium text-gray-600">Consideration</td>
                          <td className="px-3 py-2 text-gray-900">₹{localOrder.consideration_amount}</td>
                        </tr>
                      )}
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-600">Delivery Mode</td>
                        <td className="px-3 py-2 text-gray-900 capitalize">{localOrder.delivery_mode}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* First Party */}
                <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                  <p className="font-medium text-blue-900 mb-2 text-sm flex items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2"></span>
                    First Party
                  </p>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-gray-900">{localOrder.first_party.name}</p>
                    <p className="text-gray-600 text-xs">{localOrder.first_party.address}</p>
                    <p className="text-gray-600 text-xs flex items-center mt-1">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      {localOrder.first_party.mobile}
                    </p>
                  </div>
                </div>

                {/* Second Party */}
                <div className="bg-green-50/50 p-3 rounded-lg border border-green-100">
                  <p className="font-medium text-green-900 mb-2 text-sm flex items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2"></span>
                    Second Party
                  </p>
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-gray-900">{localOrder.second_party?.name || '-'}</p>
                    <p className="text-gray-600 text-xs">{localOrder.second_party?.address || '-'}</p>
                    <p className="text-gray-600 text-xs flex items-center mt-1">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      {localOrder.second_party?.mobile || '-'}
                    </p>
                  </div>
                </div>

                {/* Notes */}
                {localOrder.notes && (
                  <div className="bg-amber-50/50 p-3 rounded-lg border border-amber-100">
                    <p className="font-medium text-amber-900 mb-2 text-sm">Notes</p>
                    <p className="text-sm text-gray-700">{localOrder.notes}</p>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN: Redesigned Compact Preview Box */}
              <div className="flex flex-col">
                {/* Approval Actions (Moved from Overlay) */}
                {!selectedSampleFile && ((user.role === 'client' && localOrder.client_id === user.id) || user.role === 'admin') && localOrder.status === 'sample_submitted' && (
                  <div className="mb-4 flex space-x-3 justify-start">
                    <button
                      onClick={() => onApproveRejectSample(localOrder.id, true)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center hover-lift"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Approve
                    </button>
                    <button
                      onClick={() => onApproveRejectSample(localOrder.id, false)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center hover-lift"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Reject
                    </button>
                  </div>
                )}

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-96 relative">
                  {renderEmbeddedSample()}
                </div>

                {/* Helper text below box */}
                <div className="mt-2 text-center">
                  {localOrder.status === 'sample_submitted' && ((user.role === 'client' && localOrder.client_id === user.id) || user.role === 'admin') && (
                    <p className="text-xs text-gray-500">Review the sample above and approve or reject.</p>
                  )}
                  {localOrder.status === 'sample_rejected' && (
                    <p className="text-xs text-red-500 font-medium">Sample rejected. Please upload a new version.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'api-documents':
        return <DocumentSplitView order={localOrder} />;

      case 'upload-jobs':
        return (
          <div className="px-6 pb-6 pt-2">
            <UploadJobsPanel orderId={localOrder.id} />
          </div>
        );

      case 'full-document':
        const fullOrderFiles = [...(localOrder.full_order_document_files || [])].sort(
          (a, b) => new Date(a.upload_date) - new Date(b.upload_date)
        );
        const hasLegacyFile = localOrder.full_order_document_file;
        const visibleFiles = user.role === 'client'
          ? fullOrderFiles.filter(file => file.approval_status === 'approved')
          : fullOrderFiles;

        // Calculate total stamps uploaded (accounts for double-sided documents)
        const totalStampsUploaded = fullOrderFiles.reduce(
          (sum, file) => sum + (file.stamp_count || file.page_count || 0), 0
        );
        const isQuantityComplete = totalStampsUploaded >= localOrder.quantity;

        return (
          <div className="px-6 pb-6 pt-2">
            <div className="space-y-6">
              {(user.role === 'admin' ||
                (user.role === 'vendor' && localOrder.vendor_id === user.id && localOrder.status === 'sample_approved')) && (
                  <div className={`p-4 rounded-lg ${isQuantityComplete ? 'bg-green-50 border border-green-200' : 'bg-indigo-50'}`}>
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="font-medium">{isQuantityComplete ? '✅ All stamps uploaded' : 'Upload Full Order Documents'}</h5>
                      <div className={`text-sm ${isQuantityComplete ? 'text-green-700 font-semibold' : 'text-indigo-700'}`}>
                        Stamps: {totalStampsUploaded} / {localOrder.quantity}
                      </div>
                    </div>
                    {isQuantityComplete && (
                      <p className="text-sm text-green-700 mb-2">
                        The required quantity of {localOrder.quantity} stamp(s) has been uploaded. No further uploads are needed.
                      </p>
                    )}
                    <button
                      onClick={() => setShowUploadDialog(true)}
                      className={`btn-primary px-4 py-2 ${(uploading || isQuantityComplete) ? 'opacity-50 cursor-not-allowed hover:bg-none pointer-events-auto' : ''}`}
                      disabled={uploading || isQuantityComplete}
                      style={(uploading || isQuantityComplete) ? { pointerEvents: 'auto', cursor: 'not-allowed' } : {}}
                    >
                      Upload Files
                    </button>
                  </div>
                )}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h4 className="text-lg font-semibold">Full Order Documents</h4>
                  <p className="text-sm text-gray-600">
                    Stamps: {totalStampsUploaded} / {localOrder.quantity} · Pages: {localOrder.total_pages_uploaded || 0}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
                    >
                      📋 List
                    </button>
                    <button
                      onClick={() => setViewMode('gallery')}
                      className={`px-3 py-1 rounded ${viewMode === 'gallery' ? 'bg-white shadow' : ''}`}
                    >
                      🖼️ Gallery
                    </button>
                  </div>
                </div>
              </div>
              {hasLegacyFile && (
                <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h5 className="font-medium text-yellow-800 mb-2">Legacy Document</h5>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePDFPreview(localOrder.full_order_document_file, localOrder.full_order_document_filename)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Preview
                    </button>
                    <button
                      onClick={() => handleLegacyFileDownload(localOrder.full_order_document_file, localOrder.full_order_document_filename)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
              )}
              {visibleFiles.length > 0 ? (
                <div>
                  {visibleFiles.length > 0 && (
                    <div className="mb-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedFiles.length === visibleFiles.length && visibleFiles.length > 0}
                            onChange={() => handleSelectAll(visibleFiles)}
                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            Select All ({selectedFiles.length}/{visibleFiles.length})
                          </span>
                        </label>
                      </div>
                      {selectedFiles.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleBulkDownload}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Download
                          </button>
                          {user.role === 'admin' && (
                            <>
                              <button
                                onClick={handleBulkApprove}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm"
                              >
                                ✓ Approve
                              </button>
                              <button
                                onClick={handleBulkReject}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-sm"
                              >
                                ✗ Reject
                              </button>
                              <button
                                onClick={handleBulkDelete}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-sm"
                              >
                                🗑 Delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {viewMode === 'list' ? (
                    <div className="space-y-3">
                      {visibleFiles.map((file, index) => (
                        <div key={file.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={selectedFiles.includes(file.id)}
                              onChange={() => handleSelectFile(file.id)}
                              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                            />
                            <div className="bg-red-100 p-2 rounded">
                              <span className="text-red-600">📄</span>
                            </div>
                            <div>
                              <p className="font-medium">{file.filename}</p>
                              <p className="text-sm text-gray-600">
                                {file.page_count} pages • {file.pages_per_stamp === 2 ? 'Double-sided' : 'Single-sided'} •
                                Stamps: {file.stamp_count} • Uploaded: {new Date(file.upload_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </p>
                              {user.role !== 'client' && (
                                <span className={`inline-block px-2 py-1 rounded-full text-xs ${file.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                                  file.approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                  {file.approval_status}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 relative">
                            <button
                              onClick={() => handleFullDocumentPreview(localOrder.id, file.id, file.filename)}
                              className="btn-primary px-3 py-2 text-sm flex items-center relative"
                              disabled={loadingFiles[file.id]}
                            >
                              {loadingFiles[file.id] ? (
                                <>
                                  <svg
                                    className="animate-spin h-5 w-5 mr-2 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Loading...
                                </>
                              ) : (
                                'Preview'
                              )}
                            </button>
                            <button
                              onClick={() => handleFileDownload(localOrder.id, file.id, file.filename)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 text-sm rounded-md flex items-center relative"
                              disabled={loadingFiles[`${file.id}_download`]}
                            >
                              {loadingFiles[`${file.id}_download`] ? (
                                <>
                                  <svg
                                    className="animate-spin h-5 w-5 mr-2 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Download
                                </>
                              )}
                            </button>
                            {loadingFiles[file.id] && (
                              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10">
                                Please wait
                              </div>
                            )}
                            {user.role === 'admin' && (
                              <>
                                {file.approval_status === 'pending' && (
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => handleFileApproval(file.id, true)}
                                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleFileApproval(file.id, false)}
                                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                )}
                                <button
                                  onClick={() => handleFileDelete(file.id)}
                                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {visibleFiles.map((file, index) => (
                        <div key={file.id} className="border border-gray-200 rounded-lg p-3 text-center">
                          <div className="bg-red-100 p-4 rounded-lg mb-3">
                            <span className="text-red-600 text-2xl">📄</span>
                          </div>
                          <p className="font-medium text-sm mb-1 truncate" title={file.filename}>
                            {file.filename}
                          </p>
                          <p className="text-xs text-gray-600 mb-2">
                            {file.page_count} pages • {file.pages_per_stamp === 2 ? 'Double-sided' : 'Single-sided'} •
                            Stamps: {file.stamp_count} • Uploaded: {new Date(file.upload_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </p>
                          {user.role !== 'client' && (
                            <span className={`inline-block px-2 py-1 rounded-full text-xs mb-2 ${file.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                              file.approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                              {file.approval_status}
                            </span>
                          )}
                          <div className="space-y-1 relative">
                            <button
                              onClick={() => handleFullDocumentPreview(localOrder.id, file.id, file.filename)}
                              className="btn-primary px-2 py-1 text-xs w-full flex items-center justify-center"
                              disabled={loadingFiles[file.id]}
                            >
                              {loadingFiles[file.id] ? (
                                <>
                                  <svg
                                    className="animate-spin h-4 w-4 mr-2 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Loading...
                                </>
                              ) : (
                                'Preview'
                              )}
                            </button>
                            <button
                              onClick={() => handleFileDownload(localOrder.id, file.id, file.filename)}
                              className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs w-full rounded-md flex items-center justify-center"
                              disabled={loadingFiles[`${file.id}_download`]}
                            >
                              {loadingFiles[`${file.id}_download`] ? (
                                <>
                                  <svg
                                    className="animate-spin h-4 w-4 mr-2 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Download
                                </>
                              )}
                            </button>
                            {loadingFiles[file.id] && (
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 z-10">
                                Please wait
                              </div>
                            )}
                            {user.role === 'admin' && (
                              <div className="flex space-x-1">
                                {file.approval_status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleFileApproval(file.id, true)}
                                      className="bg-green-600 text-white px-2 py-1 rounded text-xs flex-1 hover:bg-green-700"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={() => handleFileApproval(file.id, false)}
                                      className="bg-red-600 text-white px-2 py-1 rounded text-xs flex-1 hover:bg-red-700"
                                    >
                                      ✗
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleFileDelete(file.id)}
                                  className="bg-red-600 text-white px-2 py-1 rounded text-xs w-full hover:bg-red-700"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No documents uploaded yet</p>
                  {user.role === 'client' && fullOrderFiles.length > 0 && (
                    <p className="text-sm mt-2">Documents are pending admin approval</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'pod':
        return (
          <div className="p-1">
            <div className="text-center space-y-4">
              <h4 className="text-lg font-semibold">Proof of Delivery</h4>
              <button
                onClick={() => handlePodPreview(localOrder.pod_file, localOrder.pod_filename)}
                className="btn-primary px-6 py-3 transition-colors"
              >
                Preview POD
              </button>
            </div>
          </div>
        );

      case 'actions':
        return (
          <div className="px-4 pb-4 pt-2 space-y-4">
            <h4 className="text-lg font-semibold">Available Actions</h4>
            <div className="space-y-4">
              {((user.role === 'admin') || (user.role === 'client' && localOrder.client_id === user.id)) &&
                localOrder.status !== 'sample_approved' && localOrder.status !== 'completed' && localOrder.status !== 'delivered' && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium mb-3">Edit Order</h5>
                    <button
                      onClick={() => setShowEditForm(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Edit Order Details
                    </button>
                  </div>
                )}
              {((user.role === 'admin') || (user.role === 'client' && localOrder.client_id === user.id)) &&
                localOrder.status !== 'sample_approved' && localOrder.status !== 'completed' && localOrder.status !== 'delivered' && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h5 className="font-medium mb-3">Reject Order</h5>
                    <button
                      onClick={() => setShowRejectForm(true)}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                    >
                      Reject Order
                    </button>
                  </div>
                )}
              {user.role === 'admin' && localOrder.status !== 'completed' && localOrder.status !== 'delivered' && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-medium mb-3">Complete Order</h5>
                  <button
                    onClick={() => onCompleteOrder(localOrder.id)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                  >
                    Mark as Completed
                  </button>
                </div>
              )}
            </div>
            {user.role === 'admin' && (
              <div className="space-y-4">
                {localOrder.status === 'placed' && !localOrder.vendor_id ? (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium mb-3">Assign Order to Vendor</h5>
                    <div className="flex space-x-3">
                      <select
                        value={selectedVendor}
                        onChange={(e) => setSelectedVendor(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-md"
                      >
                        <option value="">Select Vendor</option>
                        {vendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleAssignOrder}
                        disabled={!selectedVendor}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        Assign
                      </button>
                    </div>
                  </div>
                ) : localOrder.vendor_id && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h5 className="font-medium text-green-900 mb-2">Order Assignment Status</h5>
                    <p className="text-green-800">✅ Order assigned to: <span className="font-semibold">{localOrder.vendor_name}</span></p>
                    <p className="text-sm text-green-700 mt-1">Status: {localOrder.status.replace('_', ' ')}</p>
                  </div>
                )}
              </div>
            )}
            {user.role === 'vendor' && localOrder.vendor_id === user.id && (
              <div className="space-y-4">
                {(localOrder.delivery_mode === 'physical' || localOrder.delivery_mode === 'both') && !localOrder.pod_file && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h5 className="font-medium mb-3">Upload Proof of Delivery</h5>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handlePodSelect(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                      disabled={uploading}
                    />
                    {uploading && <p className="text-sm text-gray-600 mt-2">Uploading...</p>}
                  </div>
                )}
                {(localOrder.document_file && localOrder.pod_file) && (
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <h5 className="font-medium text-indigo-900 mb-2">Order Completion Status</h5>
                    <p className="text-indigo-800">🎯 All required files uploaded! Order is ready for delivery.</p>
                  </div>
                )}
                {localOrder.pod_file && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h5 className="font-medium text-purple-900 mb-2">Proof of Delivery</h5>
                    <p className="text-sm text-purple-800 mb-3">{localOrder.pod_filename || 'POD Uploaded'}</p>
                    <button
                      onClick={() => handlePodPreview(localOrder.pod_file, localOrder.pod_filename)}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm transition-colors"
                    >
                      Preview POD
                    </button>
                  </div>
                )}
              </div>
            )}
            {user.role === 'admin' && !localOrder.pod_file && (
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h5 className="font-medium mb-3">Upload POD (Admin)</h5>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handlePodSelect(e.target.files[0])}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  disabled={uploading}
                />
                {uploading && <p className="text-sm text-gray-600 mt-2">Uploading...</p>}
              </div>
            )}
            {user.role === 'admin' && localOrder.pod_file && (
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h5 className="font-medium text-indigo-900 mb-2">Proof of Delivery</h5>
                <p className="text-sm text-indigo-800 mb-3">{localOrder.pod_filename || 'POD Uploaded'}</p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePodPreview(localOrder.pod_file, localOrder.pod_filename)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm transition-colors"
                  >
                    Preview
                  </button>
                  <label className="bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 px-4 py-2 rounded-md text-sm transition-colors cursor-pointer">
                    Replace POD
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => handlePodSelect(e.target.files[0])}
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="max-w-6xl"
    >
      {/* Fixed Header - Back Button + Tabs */}
      <div className="flex-shrink-0 px-6 pt-3 pb-0 bg-white">
        {/* Back Button */}
        <button
          onClick={onClose}
          className="mb-2 p-2 -ml-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
          aria-label="Back"
          title="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>

        <Tabs
          tabs={getTabs()}
          activeTab={activeTab}
          onTabChange={(tabId) => {
            setActiveTab(tabId);
            setViewingFile(null); // Reset file preview when switching tabs
          }}
        />
        {loadingDetails && (
          <div className="mt-2 flex items-center text-sm text-gray-500 gap-2">
            <svg className="w-4 h-4 animate-spin text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span>Loading the latest order files...</span>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
        {showSampleConfirmation && selectedSampleFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Confirm Sample Upload</h3>
              <p className="text-on-light mb-4">Do you want to upload this sample?</p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm"><strong>File:</strong> {selectedSampleFile.name}</p>
                <p className="text-sm"><strong>Size:</strong> {(selectedSampleFile.size / 1024).toFixed(1)} KB</p>
                <p className="text-sm"><strong>Type:</strong> {selectedSampleFile.type}</p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowSampleConfirmation(false);
                    setSelectedSampleFile(null);
                  }}
                  className="px-4 py-2 text-on-light border border-gray-300 rounded-lg hover:bg-gray-50 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSampleUpload}
                  disabled={uploading}
                  className="btn-primary disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Sample'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showUploadDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Upload Full Order Documents</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">
                    Select Files <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    disabled={uploading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">
                    Pages per Stamp <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={pagesPerStamp}
                    onChange={(e) => setPagesPerStamp(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={uploading}
                  >
                    <option value="1">1 (Single-sided)</option>
                    <option value="2">2 (Double-sided)</option>
                  </select>
                </div>
                {/* Admin-only: Certificate ID CSV upload */}
                {user.role === 'admin' && (
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <label className="block text-sm font-medium text-yellow-800 mb-2">
                      Certificate ID Mapping (CSV) - Optional
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setCsvFile(e.target.files[0] || null)}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-yellow-100 file:text-yellow-800 hover:file:bg-yellow-200"
                      disabled={uploading}
                    />
                    <p className="text-xs text-yellow-700 mt-1">
                      CSV should have a "certificate_id" column. Rows map to PDF pages sequentially.
                    </p>
                    {csvFile && (
                      <p className="text-xs text-green-700 mt-1">✓ {csvFile.name} selected</p>
                    )}
                  </div>
                )}
                {uploading && <p className="text-sm text-gray-600">Uploading...</p>}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowUploadDialog(false);
                    setUploadFiles([]);
                    setPagesPerStamp('1');
                    setCsvFile(null); // Reset CSV file
                  }}
                  className="px-4 py-2 text-on-light border border-gray-300 rounded-lg hover:bg-gray-50 btn-secondary"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleFileUpload('full-document', uploadFiles, parseInt(pagesPerStamp))}
                  disabled={uploading || uploadFiles.length === 0}
                  className="btn-primary disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showEditForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Edit Order</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                onEditOrder(localOrder.id, editFormData);
                setShowEditForm(false);
              }} className="space-y-6">
                <div>
                  <h4 className="font-semibold text-on-light mb-3">Order Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">Article <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={editFormData.article}
                        onChange={(e) => setEditFormData({ ...editFormData, article: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">State <span className="text-red-500">*</span></label>
                      <select
                        value={editFormData.state}
                        onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="">Select State</option>
                        {indianStates.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">Quantity <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        value={editFormData.quantity}
                        onChange={(e) => setEditFormData({ ...editFormData, quantity: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        min="1"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-on-light mb-1">Denomination (₹) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        value={editFormData.denomination}
                        onChange={(e) => setEditFormData({ ...editFormData, denomination: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-on-light mb-1">Delivery Mode <span className="text-red-500">*</span></label>
                      <select
                        value={editFormData.delivery_mode}
                        onChange={(e) => setEditFormData({ ...editFormData, delivery_mode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="digital">Digital</option>
                        <option value="physical">Physical</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-on-light mb-1">Notes</label>
                      <textarea
                        value={editFormData.notes}
                        onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-on-light mb-3">Party Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-medium text-black mb-3">First Party</p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-on-light mb-1">Name <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            value={editFormData.first_party.name}
                            onChange={(e) => setEditFormData({
                              ...editFormData,
                              first_party: { ...editFormData.first_party, name: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-on-light mb-1">Address</label>
                          <textarea
                            value={editFormData.first_party.address}
                            onChange={(e) => setEditFormData({
                              ...editFormData,
                              first_party: { ...editFormData.first_party, address: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows="2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-on-light mb-1">Mobile <span className="text-red-500">*</span></label>
                          <input
                            type="tel"
                            value={editFormData.first_party.mobile}
                            onChange={(e) => setEditFormData({
                              ...editFormData,
                              first_party: { ...editFormData.first_party, mobile: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            pattern="[0-9]{10}"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="font-medium text-black mb-3">Second Party</p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-on-light mb-1">Name</label>
                          <input
                            type="text"
                            value={editFormData.second_party.name}
                            onChange={(e) => setEditFormData({
                              ...editFormData,
                              second_party: { ...editFormData.second_party, name: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-on-light mb-1">Address</label>
                          <textarea
                            value={editFormData.second_party.address}
                            onChange={(e) => setEditFormData({
                              ...editFormData,
                              second_party: { ...editFormData.second_party, address: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            rows="2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-on-light mb-1">Mobile</label>
                          <input
                            type="tel"
                            value={editFormData.second_party.mobile}
                            onChange={(e) => setEditFormData({
                              ...editFormData,
                              second_party: { ...editFormData.second_party, mobile: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            pattern="[0-9]{10}"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditForm(false)}
                    className="px-4 py-2 text-on-light border border-gray-300 rounded-lg hover:bg-gray-50 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Update Order
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {showRejectForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Reject Order</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (rejectionReason.trim()) {
                  onRejectOrder(localOrder.id, rejectionReason);
                  setShowRejectForm(false);
                  setRejectionReason('');
                  onClose();
                }
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-on-light mb-1">
                    Cancellation Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a reason for rejecting this order..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows="4"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRejectForm(false);
                      setRejectionReason('');
                    }}
                    className="px-4 py-2 text-on-light border border-gray-300 rounded-lg hover:bg-gray-50 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Reject Order
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPodConfirmation && selectedPodFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Confirm POD Upload</h3>
              <p className="text-on-light mb-4">Do you want to upload this Proof of Delivery?</p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4 space-y-2">
                {/* Inline thumbnail preview */}
                {selectedPodFile.type.startsWith('image/') ? (
                  <div className="mb-3 flex justify-center">
                    <img
                      src={selectedPodFile.url}
                      alt="POD Preview"
                      className="max-h-48 max-w-full rounded-lg border border-gray-200 object-contain"
                    />
                  </div>
                ) : (
                  <div className="mb-3 flex justify-center">
                    <div className="bg-red-100 p-6 rounded-lg">
                      <span className="text-red-600 text-4xl">📄</span>
                    </div>
                  </div>
                )}
                <p className="text-sm"><strong>File:</strong> {selectedPodFile.name}</p>
                <p className="text-sm"><strong>Size:</strong> {(selectedPodFile.size / 1024).toFixed(1)} KB</p>
                <p className="text-sm"><strong>Type:</strong> {selectedPodFile.type}</p>
                <button
                  onClick={() => handlePodPreview(null, selectedPodFile.name, selectedPodFile.url)}
                  className="mt-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {selectedPodFile.type.startsWith('image/') ? 'View Full Size' : 'Preview PDF'}
                </button>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handlePodCancel}
                  className="px-4 py-2 text-on-light border border-gray-300 rounded-lg hover:bg-gray-50 btn-secondary"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPodUpload}
                  disabled={uploading}
                  className="btn-primary disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload POD'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showImageViewer && imageViewerData.url && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center z-[60]">
            <div className="w-full flex items-center justify-between px-6 py-3 bg-black/50">
              <h3 className="text-white text-lg font-semibold truncate">{imageViewerData.title || 'Image Preview'}</h3>
              <button
                onClick={handleCloseImageViewer}
                className="text-white hover:text-gray-300 p-2 rounded-full hover:bg-white/10 transition-colors"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 w-full max-w-5xl mx-auto p-4" style={{ minHeight: 0 }}>
              <ImageViewer
                src={imageViewerData.url}
                alt={imageViewerData.title || 'POD Image'}
                fileName={imageViewerData.title}
              />
            </div>
          </div>
        )}

        <PDFViewer
          isOpen={showPDFViewer}
          onClose={handleClosePDFViewer}
          pdfUrl={pdfViewerData.url}
          title={pdfViewerData.title}
        />
      </div>
    </Modal>
  );
};

export default OrderModal;